import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Container, Spinner } from "reactstrap";
import { Button, Modal as BootstrapModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Row, Col, Divider, Input, Space, Tag } from "antd";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { filterObject, formatDate, PickKeyPairs } from "../../../utils/helpers";
import { useBasicDataQuery } from "../../../services/basicData/basicData";
import {
  useGetPilgrimsQuery,
  useAccommodatePilgrimsMutation,
  useCancelAccommodationMutation,
} from "../../../services/accommodations";
import { useGetOneArrivalSegmentQuery } from "../../../services/arrivals";
import { useGetOneMakkahGroupSegmentQuery } from "../../../services/groups";
import { useAccommodationSelection } from "./hooks/useAccommodationSelection";
import AccommodationTable from "./AccommodationTable";
import { useDebouncedSearch } from "../../../utils/hooks/useDebouncedSearch";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { LabelValue } from "@/Component/Value";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { SelectStickyAction } from "@/Component/SelectStickyAction";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";

const CampaignSettlementPage = () => {
  const formRef = useRef()
  const { t, i18n } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { data: basicDataRes } = useBasicDataQuery();
  const basicData = useMemo(() => basicDataRes?.data?.data ?? {}, [basicDataRes]);
  const { currentSystem, port } = useSystem();

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showAccommodateModal, setShowAccommodateModal] = useState(false);
  const [selectedOnePilgrims, setSelectedPilgrims] = useState(false);
  const [showClearSelectionConfirm, setShowClearSelectionConfirm] =
    useState(false);

  // Debounced search for pilgrims table
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    1200
  );

  // Filter state for dynamic filters
  const [filter, setFilter] = useState({
    is_accommodated: 0,
  });

  const filterValues = {
    page: currentPage,
    per_page: limit,
    search: debouncedValue,
    type,
    ...filter,
  };

  // Form management with React Hook Form
  const form = useForm({
    defaultValues: {
      cabinet_number: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  // Selection hook for managing pilgrim selection
  const selectionHook = useAccommodationSelection();
  const { selectedPilgrims, selectedCount, clearSelection } = selectionHook;

  // Conditionally fetch data based on type
  const {
    data: campaignRes,
    isLoading: campaignLoading,
    isError: campaignError,
  } = useGetOneArrivalSegmentQuery({
    id,
    enabled: type === "campaign",
    currentSystem,
  });

  const {
    data: groupRes,
    isLoading: groupLoading,
    isError: groupError,
  } = useGetOneMakkahGroupSegmentQuery(type === "group" ? id : null);

  // Determine which data to use
  const isLoading = type === "campaign" ? campaignLoading : groupLoading;
  const isError = type === "campaign" ? campaignError : groupError;
  const arrival =
    type === "campaign" ? campaignRes?.data?.data : groupRes?.data?.data;

  const FieldRow = useCallback(
    ({ label, value, ...props }) => (
      <Col className="mb-3" xs={12} sm={8} md={8} lg={8}>
        <LabelValue
          loading={isLoading}
          direction="vertical"
          label={label}
          value={value}
          {...props}
        />
      </Col>
    ),
    [isLoading]
  );

  const {
    data: pilgrimsRes,
    isLoading: pilgrimsLoading,
    isFetching: pilgrimsFetching,
  } = useGetPilgrimsQuery({
    ...filterValues,
    enabled: !!type && !!id,
  });

  // Accommodation mutation
  const accommodateMutation = useAccommodatePilgrimsMutation(filterValues);

  // Cancel accommodation mutation
  const cancelAccommodateMutation =
    useCancelAccommodationMutation(filterValues);

  const isAccommodating =
    accommodateMutation.isPending || cancelAccommodateMutation.isPending;

  const mutationsVariables = accommodateMutation.isPending
    ? accommodateMutation.variables
    : cancelAccommodateMutation.isPending
      ? cancelAccommodateMutation.variables
      : null;

  const pilgrims = pilgrimsRes?.data?.data || [];
  const applied_filters = pilgrimsRes?.data?.applied_filters || null;

  useEffect(() => {
    const filters = type === 'group' && applied_filters ? PickKeyPairs(applied_filters, ['group_number', 'company_id', 'mission_id', 'nationality_id']) : PickKeyPairs(applied_filters, ['campaign_id', 'nationality_id']);

    if (applied_filters)
      setFilter((prev) => ({
        ...prev,
        ...(filters || {}),
        ...filterObject({ nationality_id: filters?.nationality_id ? String(filters?.nationality_id) : 0 }),
      }));
  }, [applied_filters, type]);

  const pilgrimsTotal = pilgrimsRes?.data?.meta?.total || 0;

  const directionDisplay = useMemo(() => {
    return (
      basicData?.directions?.find(
        (r) => String(r.id) === String(arrival?.direction_id)
      )?.name || "--"
    );
  }, [basicData, arrival?.direction_id]);

  const airlineDisplay = useMemo(() => {
    return (
      basicData?.airlines?.find(
        (r) => String(r.id) === String(arrival?.airline_id)
      )?.name || "-"
    );
  }, [basicData, arrival?.airline_id]);

  const totalCapacity = pilgrimsRes?.data?.total ?? 0;
  const numberOfAccommodated = arrival?.number_of_accommodated ?? 0;
  const numberOfNotAccommodated = Math.max(
    0,
    totalCapacity - numberOfAccommodated
  );

  const direction =
    basicData?.directions?.find(
      (r) => String(r.id) === String(arrival?.coming_from_id)
    ) || null;

  // Get display values based on type
  const displaySerial = arrival?.number;

  const displayPilgrimsCount = arrival?.capacity;

  const formattedDate = formatDate(
    arrival?.flight_date,
    i18n.language,
    "DD - MMMM YYYY"
  );
  const formattedTime = arrival?.real_datetime
    ?
    dayjs(arrival?.real_datetime, "YYYY-MM-DD HH:mm:ss").format("HH:mm")
    : "--";

  // Handle search change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter change from AccommodationTable
  const handleFilterChange = (newFilters) => {
    setFilter(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const hasCabinetNumber = !!arrival?.accommodation?.cabinet_number

  // Close page and navigate back
  const handleClosePage = () => {
    clearSelection();
    reset();
    setSearchValue("");
    setCurrentPage(1);
    setShowCloseConfirm(false);
    navigate(-1);
  };

  // Handle opening the accommodation modal
  const handleOpenAccommodateModal = () => {
    // Validate selection
    if (selectedCount === 0) {
      toast.error(t("NO_PILGRIMS_SELECTED"));
      return;
    }
    if (hasCabinetNumber) {
      const formValues = form.getValues()
      onSubmit(formValues)
      return;
    }

    setShowAccommodateModal(true);
  };

  // Handle clear selection with confirmation
  const handleClearSelectionClick = () => {
    setShowClearSelectionConfirm(true);
  };

  // Confirm clear selection
  const handleConfirmClearSelection = () => {
    clearSelection();
    setShowClearSelectionConfirm(false);
  };

  // Handle form submission
  const onSubmit = (formData) => {
    // Prepare payload
    const payload = {
      type: type, // Use the type from URL params
      id: arrival?.id,
      cabinet_number: formData.cabinet_number || null,
      pilgrims: (selectedOnePilgrims ? [selectedOnePilgrims] : selectedPilgrims).map((p) => ({
        pilgrim_uid: p.pilgrim_uid,
        passport_number: p.passport_number,
        full_name_ar: p.full_name_ar,
        full_name_en: p.full_name_en,
        contract_id: p.contract_id,
        id: p.id,
      })),
    };

    // Submit accommodation
    accommodateMutation.mutate(payload, {
      onSuccess: () => {
        clearSelection();
        reset();
        setSelectedPilgrims(null)
        setShowAccommodateModal(false);
      },
    });
  };

  // Handle single pilgrim accommodation from table action button
  const handleSingleAccommodate = (pilgrim) => {

    if (!hasCabinetNumber) {
      setShowAccommodateModal(true)
      setSelectedPilgrims(pilgrim)
      return;
    }

    const payload = {
      type: type, // Use the type from URL params
      id: arrival?.id,
      cabinet_number: null,
      pilgrims: [
        {
          pilgrim_uid: pilgrim.pilgrim_uid,
          passport_number: pilgrim.passport_number,
          full_name_ar: pilgrim.full_name_ar,
          full_name_en: pilgrim.full_name_en,
          contract_id: pilgrim.contract_id,
          id: pilgrim.id,
        },
      ],
    };

    accommodateMutation.mutate(payload);
  };

  // Handle cancel accommodation
  const handleCancelAccommodate = (pilgrim) => {
    const payload = {
      type: type, // Use the type from URL params
      id: arrival?.id,
      pilgrims: [
        {
          id: pilgrim.id,
          pilgrim_uid: pilgrim.pilgrim_uid,
        },
      ],
    };

    cancelAccommodateMutation.mutate(payload);
  };

  const isGroup = type === "group";

  const mapLink = ({ currentSystem, id, port }) => ({
    campaign: `/app/${currentSystem}/arrival-campaign/${id}/${port}/view`,
    group: `/app/${currentSystem}/groups/${id}/view`,
  })

  const numberOfNotAccommodatedDisplay = Number(arrival?.capacity || 0) -
    Number(arrival?.number_of_accommodated || 0)

  return (
    <>
      <PageHeader title={t("ACCOMMODATE_PILGRIMS")} />
      <Container fluid>
        {/* Arrival Information */}
        <Row>
          <FieldRow
            label={t(`ARRIVAL_CARD_${type.toUpperCase()}_NUMBER`)}
            value={
              arrival?.name ? <Link to={mapLink({ currentSystem, id: arrival?.parent_id, port })[type]}>
                {`${[displaySerial, arrival?.name].filter(Boolean).join(" - ")}`}
              </Link> :
                "-"
            }
          />
          <FieldRow
            label={t("ARRIVAL_CARD_PILGRIMS_COUNT")}
            value={displayPilgrimsCount || "-"}
          />
          <FieldRow
            label={t("ARRIVAL_CARD_FLIGHT_DATE")}
            value={formattedDate}
          />
          <FieldRow
            label={t("ARRIVAL_CARD_ACTUAL_ARRIVAL_TIME")}
            value={formattedTime}
          />
          {isGroup && <FieldRow
            label={t("ORGANIZER")}
            renderValue={() =>
              <SelectCachedOptions.Display
                value={arrival?.missions_id}
                optionKey="missions"
                campareBy="id"
                render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en) || "-"].filter(Boolean).join(" - ")} />
            }
          />}
          {isGroup && <FieldRow
            label={t("AFFILIATED_ORGANIZER")}
            renderValue={() => <SelectCachedOptions.Display
              value={arrival?.company_id}
              optionKey="companies"
              campareBy="id"
              render={(option) => {
                const label = [option?.id, getLocalizedText(option?.name_ar, option?.name_en) || "-"].filter(Boolean).join(" - ")
                return label;
              }} />
            }
          />}
          <FieldRow label={t("ENTRY_PORT")} value={getLocalizedText(arrival?.entry_port?.name_ar, arrival?.entry_port?.name_en) || "-"} />
          <FieldRow
            label={t("ARRIVAL_CARD_ORIGIN")}
            value={direction?.name || "--"}
          />
          <FieldRow label={t("DIRECTION")} value={directionDisplay} />
          <FieldRow label={t("FLIGHT_AIRLINE")} value={<SelectCachedOptions.Display
            value={arrival?.airline_id}
            optionKey="airlines"
            campareBy="id"
            render={(option) => [option?.id, `${getLocalizedText(option?.name_ar, option?.name_en)} (${option?.code})` || "-"].filter(Boolean).join(" - ")} />} />
          <FieldRow label={t("FLIGHT_NUMBER")} value={arrival?.flight_number} />
          <FieldRow
            label={t("HOUSING_CONTRACT")}
            value={arrival?.contract_id}
          />
          <FieldRow
            label={t("NATIONALITY")}
            value={arrival?.nationality_id}
            renderValue={(value) => {
              return <SelectCountry.Display value={value} />;
            }}
          />
          <FieldRow
            label={t("ACCOMMODATED")}
            value={arrival?.number_of_accommodated}
          />
          <FieldRow
            label={t("NOT_ACCOMMODATED")}
            value={
              <Space>
                {numberOfNotAccommodatedDisplay}
                {numberOfNotAccommodatedDisplay < 0 && <Tag color="error">
                  {t("OVER_CAPACITY")}: {arrival?.capacity || 0}
                </Tag>}
              </Space>
            }
          />
        </Row>
        <Divider />

        {/* Accommodation Table */}
        <AccommodationTable
          isAccommodating={isAccommodating}
          mutationsVariables={mutationsVariables}
          data={pilgrims}
          loading={pilgrimsLoading || pilgrimsFetching}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          pagination={{
            current: currentPage,
            pageSize: limit,
            total: pilgrimsTotal,
            onChange: handlePageChange,
          }}
          onPageSizeChange={(newSize) => {
            setLimit(newSize);
            setCurrentPage(1);
          }}
          hasCabinetNumber={hasCabinetNumber}
          selectionHook={selectionHook}
          onAccommodate={handleSingleAccommodate}
          onCancelAccommodate={handleCancelAccommodate}
          contract_id={arrival?.contract_id}
          number_of_accommodated={numberOfAccommodated}
          total_capacity={totalCapacity}
          number_of_not_accommodated={numberOfNotAccommodated}
          mission_id={arrival?.missions_id}
          company_id={arrival?.company_id}
          onFilterChange={handleFilterChange}
          filter={filter}
        />

        {/* Action Buttons */}
        <Divider />
        <SelectStickyAction
          disabled={isAccommodating}
          pending={isAccommodating}
          modalProps={{
            showClearSelectionConfirm,
            setShowClearSelectionConfirm,
            handleConfirmClearSelection,
          }}
          selectedCount={selectedCount}
          okButtonText={t("ACCOMMODATE_PILGRIMS")}
          handleClearSelectionClick={handleClearSelectionClick}
          handleSubmitSelected={handleOpenAccommodateModal}
        />
      </Container>

      {/* Accommodation Modal */}
      <BootstrapModal
        show={showAccommodateModal}
        onHide={() => setShowAccommodateModal(false)}
        centered
      >
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>
            {t("ACCOMMODATE_PILGRIMS")}
          </BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <form ref={formRef} onSubmit={handleSubmit(onSubmit)} id="accommodation-form">
            <div>
              <label className="fw-bold">{t("CABINET_NUMBER")}</label>
              <Controller
                name="cabinet_number"
                control={control}
                render={({ field }) => (
                  <Input
                    size="large"
                    {...field}
                    type="number"
                    placeholder={t("CABINET_NUMBER")}
                    status={errors.cabinet_number ? "error" : ""}
                  />
                )}
              />
              {errors.cabinet_number && (
                <span className="text-danger small">
                  {errors.cabinet_number.message}
                </span>
              )}
            </div>
          </form>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button
            variant="secondary"
            onClick={() => {

              setSelectedPilgrims(null)
              setShowAccommodateModal(false)
            }
            }
          >
            {t("CLOSE")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="accommodation-form"
            disabled={accommodateMutation.isPending}
          >
            {accommodateMutation.isPending && (
              <Spinner size="sm" className="me-2" />
            )}
            {t("CONFIRM_ACCOMMODATION")}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* Close Confirmation Modal */}
      <BootstrapModal
        show={showCloseConfirm}
        onHide={() => setShowCloseConfirm(false)}
        centered
      >
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>
            {t("CONFIRM_CLOSE_WITH_SELECTION")}
          </BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <p>{t("SELECTION_WILL_BE_LOST")}</p>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCloseConfirm(false)}
          >
            {t("CANCEL")}
          </Button>
          <Button variant="danger" onClick={handleClosePage}>
            {t("CONFIRM")}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </>
  );
};

export default CampaignSettlementPage;
